import * as Y from 'yjs';
import { isDesktop } from '@/lib/platform';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../wiring/canvas-roots';

export const CANVAS_CLIPBOARD_MIME = 'application/x-nula-canvas';

interface ClipboardNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number } | null;
  parentId: string | null;
  rotation: number;
  locked: boolean;
  data: Record<string, unknown>;
  createdAt: number;
  createdBy: string;
  updatedAt: number;
}

interface ClipboardEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle: string | null;
  targetHandle: string | null;
  kind: 'visual' | 'logical';
  data: Record<string, unknown>;
  createdAt: number;
  createdBy: string;
  updatedAt: number;
}

interface ClipboardPayload {
  version: 1;
  nodes: ClipboardNode[];
  edges: ClipboardEdge[];
}

function plainData(yMap: Y.Map<unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  yMap.forEach((value, key) => {
    if (value instanceof Y.Text) {
      out[key] = { __ytext: true, value: value.toString() };
    } else if (value instanceof Y.Map) {
      out[key] = plainData(value);
    } else {
      out[key] = value;
    }
  });
  return out;
}

function nodeToClipboard(node: Y.Map<unknown>): ClipboardNode | null {
  const id = node.get('id');
  const type = node.get('type');
  if (typeof id !== 'string' || typeof type !== 'string') return null;
  const position = node.get('position') as { x: number; y: number } | undefined;
  const dataMap = node.get('data');
  return {
    id,
    type,
    position: position ?? { x: 0, y: 0 },
    size: (node.get('size') as ClipboardNode['size']) ?? null,
    parentId: (node.get('parentId') as string | null) ?? null,
    rotation: (node.get('rotation') as number) ?? 0,
    locked: (node.get('locked') as boolean) ?? false,
    data: dataMap instanceof Y.Map ? plainData(dataMap) : {},
    createdAt: (node.get('createdAt') as number) ?? 0,
    createdBy: (node.get('createdBy') as string) ?? '',
    updatedAt: (node.get('updatedAt') as number) ?? 0,
  };
}

function edgeToClipboard(edge: Y.Map<unknown>): ClipboardEdge | null {
  const id = edge.get('id');
  const source = edge.get('source');
  const target = edge.get('target');
  const kind = edge.get('kind');
  if (
    typeof id !== 'string' ||
    typeof source !== 'string' ||
    typeof target !== 'string' ||
    (kind !== 'visual' && kind !== 'logical')
  ) return null;
  const dataMap = edge.get('data');
  return {
    id,
    source,
    target,
    sourceHandle: (edge.get('sourceHandle') as string | null) ?? null,
    targetHandle: (edge.get('targetHandle') as string | null) ?? null,
    kind,
    data: dataMap instanceof Y.Map ? plainData(dataMap) : {},
    createdAt: (edge.get('createdAt') as number) ?? 0,
    createdBy: (edge.get('createdBy') as string) ?? '',
    updatedAt: (edge.get('updatedAt') as number) ?? 0,
  };
}

export function serializeForClipboard(
  nodes: Y.Map<unknown>[],
  edges: Y.Map<unknown>[],
): string {
  const payload: ClipboardPayload = {
    version: 1,
    nodes: nodes.map(nodeToClipboard).filter((n): n is ClipboardNode => n !== null),
    edges: edges.map(edgeToClipboard).filter((e): e is ClipboardEdge => e !== null),
  };
  return JSON.stringify(payload);
}

export interface PasteResult {
  /** Original node id → new node id assigned by paste. */
  nodeIdMap: Map<string, string>;
  /** Original edge id → new edge id. */
  edgeIdMap: Map<string, string>;
}

function newId(prefix: 'n' | 'e'): string {
  // crypto.randomUUID() is available in all evergreen browsers + Node >= 14.17.
  // Use the first 12 hex chars (~48 bits of entropy) — collision-safe under
  // human-rate paste loops on the same canvas.
  return `${prefix}_${crypto.randomUUID().slice(0, 12)}`;
}

function rebuildDataMap(plain: Record<string, unknown>): Y.Map<unknown> {
  const out = new Y.Map();
  for (const [k, v] of Object.entries(plain)) {
    if (v && typeof v === 'object' && (v as { __ytext?: boolean }).__ytext) {
      const t = new Y.Text();
      t.insert(0, (v as { value: string }).value);
      out.set(k, t);
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      // Plain {x,y} positions etc. stay plain — only Y.Text is rebuilt.
      out.set(k, v);
    } else {
      out.set(k, v);
    }
  }
  return out;
}

export interface DeserializeOptions {
  /**
   * When true, if a node's parentId was not in the paste set (i.e. the parent
   * wasn't serialized), keep the original parentId rather than nulling it.
   * Use this for in-doc duplication where the parent frame already exists.
   * Leave false (default) for clipboard paste where the parent may not exist
   * in the destination doc.
   */
  preserveOrphanParent?: boolean;
}

export function deserializeIntoYjs(
  json: string,
  doc: Y.Doc,
  /** When provided, all pasted nodes are translated so their bounding-box center lands here (canvas coords). */
  targetCenter?: { x: number; y: number },
  options?: DeserializeOptions,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): PasteResult {
  const payload = JSON.parse(json) as ClipboardPayload;
  if (payload.version !== 1) {
    throw new Error(`Unsupported canvas clipboard version: ${payload.version}`);
  }

  // Compute offset to re-center nodes around targetCenter if provided.
  let offsetX = 0;
  let offsetY = 0;
  if (targetCenter && payload.nodes.length > 0) {
    const xs = payload.nodes.map(n => n.position.x);
    const ys = payload.nodes.map(n => n.position.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs.map((x, i) => x + ((payload.nodes[i].size?.width) ?? 160)));
    const maxY = Math.max(...ys.map((y, i) => y + ((payload.nodes[i].size?.height) ?? 120)));
    const srcCenterX = (minX + maxX) / 2;
    const srcCenterY = (minY + maxY) / 2;
    offsetX = targetCenter.x - srcCenterX;
    offsetY = targetCenter.y - srcCenterY;
  }

  const nodeIdMap = new Map<string, string>();
  const edgeIdMap = new Map<string, string>();

  doc.transact(() => {
    const roots = canvasRoots(doc, binding);
    const nodes = roots.nodes;
    const edges = roots.edges;
    const zOrder = roots.zOrder;

    for (const cn of payload.nodes) {
      const nid = newId('n');
      nodeIdMap.set(cn.id, nid);
      const m = new Y.Map();
      m.set('id', nid);
      m.set('type', cn.type);
      m.set('position', { x: cn.position.x + offsetX, y: cn.position.y + offsetY });
      m.set('size', cn.size);
      // parentId is remapped in the second pass below — write null for now
      m.set('parentId', null);
      m.set('rotation', cn.rotation);
      m.set('locked', cn.locked);
      m.set('data', rebuildDataMap(cn.data));
      m.set('createdAt', Date.now());
      m.set('createdBy', cn.createdBy);
      m.set('updatedAt', Date.now());
      nodes.set(nid, m);
      zOrder.push([nid]);
    }

    // Second pass: remap parentId values using nodeIdMap.
    // If the original parentId was in the paste set, point to the new ID.
    // If it wasn't (parent wasn't pasted):
    //   - preserveOrphanParent=true  → keep original parentId (duplicate inside frame)
    //   - preserveOrphanParent=false → null (clipboard paste to unknown destination)
    for (const cn of payload.nodes) {
      if (cn.parentId !== null) {
        const nid = nodeIdMap.get(cn.id)!;
        const m = nodes.get(nid)!;
        const remappedParent = nodeIdMap.get(cn.parentId)
          ?? (options?.preserveOrphanParent ? cn.parentId : null);
        m.set('parentId', remappedParent);
        // Append to the parent frame's childOrder — last = front — so a
        // duplicate lands in front of its siblings (sibling stacking is
        // driven by childOrder, not the top-level zOrder). Mirrors
        // useAutoAttach's drag-into-frame behavior.
        if (typeof remappedParent === 'string') {
          const parent = nodes.get(remappedParent);
          const parentData = parent?.get('data');
          if (parent?.get('type') === 'frame' && parentData instanceof Y.Map) {
            const childOrder = parentData.get('childOrder');
            if (childOrder instanceof Y.Array && !childOrder.toArray().includes(nid)) {
              childOrder.push([nid]);
            }
          }
        }
      }
    }

    for (const ce of payload.edges) {
      const eid = newId('e');
      edgeIdMap.set(ce.id, eid);
      const newSource = nodeIdMap.get(ce.source);
      const newTarget = nodeIdMap.get(ce.target);
      // If endpoints aren't in the paste set, drop the edge — partial paste.
      if (!newSource || !newTarget) continue;
      const m = new Y.Map();
      m.set('id', eid);
      m.set('source', newSource);
      m.set('target', newTarget);
      m.set('sourceHandle', ce.sourceHandle);
      m.set('targetHandle', ce.targetHandle);
      m.set('kind', ce.kind);
      m.set('data', rebuildDataMap(ce.data));
      m.set('createdAt', Date.now());
      m.set('createdBy', ce.createdBy);
      m.set('updatedAt', Date.now());
      edges.set(eid, m);
    }
  }, doc.clientID);

  return { nodeIdMap, edgeIdMap };
}

export function writeCanvasToClipboard(json: string): void {
  if (isDesktop) {
    import('@tauri-apps/plugin-clipboard-manager').then(m => m.writeText(json)).catch(() => {});
  } else {
    navigator.clipboard.writeText(json).catch(() => {});
    try {
      const item = new ClipboardItem({ [CANVAS_CLIPBOARD_MIME]: new Blob([json], { type: CANVAS_CLIPBOARD_MIME }) });
      navigator.clipboard.write([item]).catch(() => {});
    } catch { /* ClipboardItem not supported in all environments */ }
  }
}
